import React, { memo, useMemo, useCallback, useState } from "react";
import { Users, Trash2 } from "lucide-react";
import EmptyState from "../generic/EmptyState";
import UserCard from "./UserCard";
import UserSearchAdd from "./UserSearchAdd";
import { Button } from "../ui/button";

const SponsoredSection = memo(
  ({
    sponsoredUsers,
    sponsoredCount,
    isEditMode = false,
    selectedSponsored = [],
    onSelectionChange,
    onDeleteSponsored,
    onAddSponsored,
  }) => {
    const [newSponsoredToAdd, setNewSponsoredToAdd] = useState([]);

    const handleUserSelection = useCallback(
      (user, isSelected) => {
        if (isSelected) {
          onSelectionChange?.([...selectedSponsored, user]);
        } else {
          onSelectionChange?.(
            selectedSponsored.filter(
              (selected) => selected.uid !== user.uid && selected.id !== user.id
            )
          );
        }
      },
      [selectedSponsored, onSelectionChange]
    );

    const isUserSelected = useCallback(
      (user) => {
        return selectedSponsored.some(
          (selected) => selected.uid === user.uid || selected.id === user.id
        );
      },
      [selectedSponsored]
    );

    const handleDeleteSelected = useCallback(() => {
      onDeleteSponsored?.(selectedSponsored);
    }, [onDeleteSponsored, selectedSponsored]);

    const handleAddNewSponsored = useCallback(
      (sponsored) => {
        onAddSponsored?.(sponsored);
        setNewSponsoredToAdd([]);
      },
      [onAddSponsored]
    );

    const sponsoredContent = useMemo(() => {
      return sponsoredUsers.length > 0 ? (
        <div className="overflow-y-auto space-y-3 max-h-60">
          {sponsoredUsers.map((sponsoredUser, index) => (
            <UserCard
              key={sponsoredUser.uid || sponsoredUser.id || index}
              user={sponsoredUser}
              isEditMode={isEditMode}
              isSelected={isUserSelected(sponsoredUser)}
              onSelectionChange={handleUserSelection}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Users}
          title="Cet utilisateur n'a parrainé personne pour le moment"
        />
      );
    }, [sponsoredUsers, isEditMode, isUserSelected, handleUserSelection]);

    const sectionTitle = useMemo(() => {
      return `Filleuls (${sponsoredCount})`;
    }, [sponsoredCount]);

    const deleteButton = useMemo(() => {
      if (!isEditMode || selectedSponsored.length === 0) return null;

      return (
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDeleteSelected}
          className="flex gap-2 items-center"
        >
          <Trash2 className="w-4 h-4" />
          Supprimer ({selectedSponsored.length})
        </Button>
      );
    }, [isEditMode, selectedSponsored.length, handleDeleteSelected]);

    const searchSection = useMemo(() => {
      if (!isEditMode) return null;

      return (
        <div className="p-4 mt-4 bg-gray-50 rounded border">
          <UserSearchAdd
            onAddUsers={handleAddNewSponsored}
            selectedUsers={newSponsoredToAdd}
            excludeUsers={sponsoredUsers}
            placeholder="Rechercher des filleuls..."
            maxSelection={10}
            sectionTitle="Ajouter des filleuls"
          />
        </div>
      );
    }, [isEditMode, sponsoredUsers, newSponsoredToAdd, handleAddNewSponsored]);

    const sectionHeader = useMemo(() => {
      return (
        <div className="flex justify-between items-center mb-3">
          <div className="flex gap-2 items-center">
            <Users className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold">{sectionTitle}</h3>
          </div>
          {deleteButton}
        </div>
      );
    }, [sectionTitle, deleteButton]);

    return (
      <div>
        {sectionHeader}
        {sponsoredContent}
        {searchSection}
      </div>
    );
  }
);

SponsoredSection.displayName = "SponsoredSection";

export default SponsoredSection;
